import { useState, useEffect } from 'react';
import { Newspaper, AlertTriangle, Calendar, ShieldAlert } from 'lucide-react';
import ModuleContainer from '../../layout/ModuleContainer.jsx';
import InfoButton from '../../common/InfoButton.jsx';

const FUTURES_CATEGORIES = ['geopolitical', 'commodities', 'monetary_policy', 'conflict', 'trade', 'macro'];

const SEVERITY_COLORS = {
  1: 'text-korinth-muted',
  2: 'text-korinth-text',
  3: 'text-korinth-warning',
  4: 'text-orange-400',
  5: 'text-korinth-danger',
};

// Upcoming economic events (static list — in future, fetch from Finnhub economic calendar)
const ECONOMIC_EVENTS = [
  { name: 'FOMC Rate Decision', frequency: '~6 weeks', impact: 'HIGH', blocked: true },
  { name: 'Non-Farm Payrolls (NFP)', frequency: '1st Friday/mo', impact: 'HIGH', blocked: true },
  { name: 'CPI Report', frequency: 'Monthly', impact: 'HIGH', blocked: true },
  { name: 'GDP Release', frequency: 'Quarterly', impact: 'MEDIUM', blocked: false },
  { name: 'PCE Inflation', frequency: 'Monthly', impact: 'MEDIUM', blocked: false },
  { name: 'Retail Sales', frequency: 'Monthly', impact: 'MEDIUM', blocked: false },
  { name: 'ISM Manufacturing', frequency: 'Monthly', impact: 'LOW', blocked: false },
];

export default function NewsCalendarTab() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNews() {
      try {
        const res = await fetch('/api/news/latest?limit=100');
        if (res.ok) {
          const all = await res.json();
          // Filter to futures-relevant categories
          setNews(all.filter(n =>
            n.severity >= 2 && FUTURES_CATEGORIES.includes(n.category)
          ));
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    fetchNews();
    const interval = setInterval(fetchNews, 60000);
    return () => clearInterval(interval);
  }, []);

  const highImpact = news.filter(n => n.severity >= 4);
  const tradingBlocked = highImpact.length > 0;

  return (
    <div className="space-y-6">
      {/* Trading blocked warning */}
      {tradingBlocked && (
        <div className="flex items-center gap-3 bg-korinth-danger/10 border border-korinth-danger/30 rounded-lg px-4 py-3">
          <ShieldAlert className="w-5 h-5 text-korinth-danger" />
          <div>
            <span className="text-xs font-mono font-bold text-korinth-danger">TRADING BLOCKED — High-Impact News Active</span>
            <p className="text-[10px] text-korinth-muted">{highImpact.length} severity 4-5 headlines detected. Avoid new entries until resolved.</p>
          </div>
          <InfoButton title="Trading Blocked">
            <p>When high-impact news (severity 4-5) is active, the setup score is capped and new signals are suppressed.</p>
            <p>This prevents entering trades that could be invalidated by macro events like FOMC, NFP, or geopolitical escalations.</p>
          </InfoButton>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* News Feed */}
        <ModuleContainer title="Futures-Relevant News" subtitle="Geopolitical, commodities, monetary policy, conflict">
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {news.length > 0 ? news.map((n, i) => {
              const color = SEVERITY_COLORS[n.severity] || 'text-korinth-muted';
              return (
                <div key={n.id || i} className="flex gap-2 py-2 border-b border-korinth-border/30 last:border-0">
                  {n.severity >= 4
                    ? <AlertTriangle className={`w-3 h-3 ${color} flex-shrink-0 mt-0.5`} />
                    : <Newspaper className="w-3 h-3 text-korinth-muted flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${color}`}>{n.headline}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[9px] text-korinth-muted font-mono">
                      <span>{n.source}</span>
                      <span className={`font-bold ${color}`}>SEV-{n.severity}</span>
                      <span className="px-1 py-0.5 rounded bg-korinth-border">{n.category}</span>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <p className="text-xs text-korinth-muted font-mono text-center py-6">
                {loading ? 'Loading...' : 'No futures-relevant news in recent feed'}
              </p>
            )}
          </div>
        </ModuleContainer>

        {/* Economic Calendar */}
        <ModuleContainer title="Economic Calendar" subtitle="High-impact events that affect futures trading">
          <div className="space-y-2">
            {ECONOMIC_EVENTS.map((ev, i) => (
              <div key={i} className={`flex items-center justify-between px-3 py-2 rounded ${
                ev.blocked ? 'bg-korinth-danger/5 border border-korinth-danger/20' : 'bg-korinth-bg border border-korinth-border/30'
              }`}>
                <div className="flex items-center gap-2">
                  <Calendar className={`w-3 h-3 ${ev.impact === 'HIGH' ? 'text-korinth-danger' : ev.impact === 'MEDIUM' ? 'text-korinth-warning' : 'text-korinth-muted'}`} />
                  <div>
                    <span className="text-xs font-mono text-white">{ev.name}</span>
                    <span className="text-[9px] text-korinth-muted ml-2">{ev.frequency}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-mono font-bold ${
                    ev.impact === 'HIGH' ? 'text-korinth-danger' : ev.impact === 'MEDIUM' ? 'text-korinth-warning' : 'text-korinth-muted'
                  }`}>{ev.impact}</span>
                  {ev.blocked && (
                    <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-korinth-danger/20 text-korinth-danger">BLOCKS SIGNALS</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[9px] text-korinth-muted font-mono mt-3">
            Exact dates fetched from Finnhub economic calendar (coming soon).
            Currently showing event types and their impact level.
          </p>
        </ModuleContainer>
      </div>
    </div>
  );
}
